<template>
  <CreatorManualMemberLeadModal
    v-model:open="showCreateLeadModal"
    mode="lead"
    @saved="handleManualLeadSaved"
  />
  <CreatorBulkImportModal
    v-model:open="showBulkImportLeadsModal"
    target-type="lead"
    @import-complete="handleBulkImportLeadsComplete"
  />
  <div class="px-4 sm:px-6 lg:px-24">
    <div class="flex flex-wrap items-center justify-between gap-3 pt-2">
      <div class="relative mx-auto max-w-[500px] flex-1">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search Leads..."
          class="w-full rounded-md border border-gray-300 px-4 py-2 pr-10 text-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-[#2D8CFF]"
        >
        <MagnifyingGlassIcon class="pointer-events-none absolute inset-y-2 right-3 h-5 w-5 text-gray-400" />
      </div>
      <div class="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          class="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-pink-500 transition hover:bg-pink-50 hover:text-pink-600"
          @click="showCreateLeadModal = true"
        >
          <UserPlusIcon class="h-6 w-6" />
          <span
            class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Create Lead
          </span>
        </button>
        <button
          type="button"
          class="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-purple-500 transition hover:bg-purple-50 hover:text-purple-600"
          @click="showBulkImportLeadsModal = true"
        >
          <ArrowUpTrayIcon class="h-6 w-6" />
          <span
            class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Upload Leads
          </span>
        </button>
        <button
          type="button"
          class="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-amber-500 transition hover:bg-amber-50 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isExportingLeads"
          @click="exportLeadsCsv"
        >
          <ArrowPathIcon
            v-if="isExportingLeads"
            class="h-6 w-6 animate-spin"
          />
          <ArrowDownTrayIcon
            v-else
            class="h-6 w-6"
          />
          <span
            class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Export Leads
          </span>
        </button>
      </div>
    </div>
    <p
      v-if="controlNoticeMessage"
      class="mt-2 text-sm text-zinc-500"
    >
      {{ controlNoticeMessage }}
    </p>

    <div class="mt-2 mx-2 flex items-end justify-end gap-2 text-right text-sm text-gray-700">
      <span class="mb-[1px]">All Leads</span>
      <span class="text-2xl font-bold text-[#2D8CFF]">{{ totalLeads }}</span>
    </div>

    <div class="mt-2 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:mx-2">
        <div class="inline-block min-w-full py-2 align-middle">
          <table
            v-if="paginatedLeads.length > 0"
            class="min-w-full divide-y divide-gray-300"
          >
            <thead>
              <tr>
                <th class="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Profile
                </th>
                <th class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Captured On
                </th>
                <th class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Source
                </th>
                <th class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Selected Plan
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr
                v-for="lead in paginatedLeads"
                :key="`lead-${lead.id}`"
                class="cursor-pointer hover:bg-zinc-100"
                @click="goToLeadDetails(lead)"
              >
                <td class="whitespace-nowrap py-5 pl-6 pr-3 text-sm">
                  <div class="flex items-center">
                    <div class="h-11 w-11 flex-shrink-0">
                      <div class="flex h-11 w-11 items-center justify-center rounded-full bg-zinc-100 text-sm font-semibold text-zinc-600">
                        {{ leadInitials(lead) }}
                      </div>
                    </div>
                    <div class="ml-4 min-w-0">
                      <div class="truncate font-medium text-gray-900">
                        {{ lead.name }}
                      </div>
                      <div class="mt-1 truncate text-gray-500">
                        {{ lead.email }}
                      </div>
                    </div>
                  </div>
                </td>

                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  <div class="text-gray-900">
                    {{ lead.createdDateLabel }}
                  </div>
                </td>
                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  <span
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                    :class="statusBadgeClass(lead.status)"
                  >
                    {{ statusLabel(lead.status) }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  {{ sourceLabel(lead.source) }}
                </td>
                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  <span class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                    {{ lead.planTierSlug || 'Not selected' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-else
            class="py-10 text-center text-sm text-gray-500"
          >
            No leads found yet.
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="totalLeads > 0"
      class="mx-2 mb-6 flex items-center justify-between border-t py-5"
    >
      <div class="text-sm text-gray-700">
        Showing
        <span class="font-medium">{{ startIndex }}</span>
        to
        <span class="font-medium">{{ endIndex }}</span>
        of
        <span class="font-medium">{{ totalLeads }}</span>
        results
      </div>
      <div class="flex items-center gap-3">
        <label class="flex items-center gap-2 text-sm text-gray-700">
          <span>Rows per page:</span>
          <PlatformaSelect
            v-model.number="itemsPerPage"
            class="min-w-[110px]"
            :options="rowsPerPageOptions"
            menu-placement="top"
          />
        </label>
        <button
          :disabled="currentPage === 1"
          class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-[10px] py-[6px] text-xs font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
          @click="prevPage"
        >
          Previous
        </button>
        <button
          :disabled="currentPage >= totalPages"
          class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-[10px] py-[6px] text-xs font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
          @click="nextPage"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { MagnifyingGlassIcon, UserPlusIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/vue/24/outline';
import CreatorManualMemberLeadModal from '@/Components/CreatorManualMemberLeadModal.vue';
import CreatorBulkImportModal from '@/Components/CreatorBulkImportModal.vue';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const router = useRouter();
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const loading = ref(false);
const leads = ref([]);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(6);
const rowsPerPageOptions = Object.freeze([
  { value: 6, label: '6' },
  { value: 12, label: '12' },
  { value: 24, label: '24' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 500, label: '500' },
]);
const showCreateLeadModal = ref(false);
const showBulkImportLeadsModal = ref(false);
const controlNoticeMessage = ref('');
const isExportingLeads = ref(false);

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

const normalizeLead = (row = {}) => {
  const firstName = String(row.firstName || '').trim();
  const lastName = String(row.lastName || '').trim();
  const name = `${firstName} ${lastName}`.trim() || 'Lead';
  return {
    id: Number(row.id || 0),
    leadPublicId: String(row.leadPublicId || '').trim(),
    name,
    firstName,
    lastName,
    email: String(row.email || '').trim(),
    status: String(row.status || '').trim().toLowerCase() || 'new',
    source: String(row.source || '').trim(),
    planTierSlug: String(row.planTierSlug || '').trim(),
    createdAt: row.createdAt || null,
    createdDateLabel: formatDateLabel(row.createdAt),
  };
};

let lifecycleRefreshTimer = null;

const fetchLeads = async () => {
  loading.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/leads`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      leads.value = [];
      return;
    }
    const rows = Array.isArray(data?.leads) ? data.leads : [];
    leads.value = rows.map(normalizeLead);
  } catch (_error) {
    leads.value = [];
  } finally {
    loading.value = false;
  }
};

const applyLeadLifecycleLocally = (payload = {}) => {
  const eventType = String(payload?.event || '').trim().toLowerCase();
  const leadPublicId = String(payload?.leadPublicId || '').trim().toLowerCase();
  const leadEmail = String(payload?.leadEmail || '').trim().toLowerCase();
  if (eventType !== 'member_joined' && eventType !== 'lead_converted') return;
  if (!leadPublicId && !leadEmail) return;

  leads.value = leads.value.filter((lead) => {
    const rowLeadPublicId = String(lead?.leadPublicId || '').trim().toLowerCase();
    const rowLeadEmail = String(lead?.email || '').trim().toLowerCase();
    if (leadPublicId && rowLeadPublicId === leadPublicId) return false;
    if (leadEmail && rowLeadEmail === leadEmail) return false;
    return true;
  });
};

const onMemberLifecycle = (event) => {
  const payload = event?.detail || {};
  applyLeadLifecycleLocally(payload);
  if (lifecycleRefreshTimer) {
    clearTimeout(lifecycleRefreshTimer);
    lifecycleRefreshTimer = null;
  }
  lifecycleRefreshTimer = setTimeout(() => {
    lifecycleRefreshTimer = null;
    void fetchLeads();
  }, 180);
};

const handleManualLeadSaved = () => {
  showCreateLeadModal.value = false;
  controlNoticeMessage.value = 'Lead saved.';
  setTimeout(() => {
    controlNoticeMessage.value = '';
  }, 3500);
  void fetchLeads();
};

const handleBulkImportLeadsComplete = (job = {}) => {
  const successRows = Number(job?.successRows || 0);
  const failedRows = Number(job?.failedRows || 0);
  const skippedRows = Number(job?.skippedRows || 0);
  controlNoticeMessage.value = `Import finished: ${successRows} success, ${failedRows} failed, ${skippedRows} skipped.`;
  setTimeout(() => {
    if (controlNoticeMessage.value.startsWith('Import finished:')) controlNoticeMessage.value = '';
  }, 6000);
  void fetchLeads();
};

const exportLeadsCsv = async () => {
  if (isExportingLeads.value) return;
  isExportingLeads.value = true;
  try {
    await new Promise((resolve) => setTimeout(resolve, 0));
    const rows = Array.isArray(filteredLeads.value) ? filteredLeads.value : [];
    const header = ['name', 'email', 'captured_on', 'status', 'source', 'selected_plan'];
    const csvRows = rows.map((lead) => [
      `"${String(lead?.name || '').replace(/"/g, '""')}"`,
      `"${String(lead?.email || '').replace(/"/g, '""')}"`,
      `"${String(lead?.createdDateLabel || '').replace(/"/g, '""')}"`,
      `"${String(statusLabel(lead?.status) || '').replace(/"/g, '""')}"`,
      `"${String(sourceLabel(lead?.source) || '').replace(/"/g, '""')}"`,
      `"${String(lead?.planTierSlug || '').replace(/"/g, '""')}"`,
    ].join(','));
    const csvText = [header.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `platforma-leads-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } finally {
    isExportingLeads.value = false;
  }
};

const filteredLeads = computed(() => {
  const query = String(searchQuery.value || '').trim().toLowerCase();
  if (!query) return leads.value;
  return leads.value.filter((lead) =>
    String(lead.name || '').toLowerCase().includes(query)
      || String(lead.email || '').toLowerCase().includes(query)
      || String(lead.planTierSlug || '').toLowerCase().includes(query)
      || String(lead.source || '').toLowerCase().includes(query)
  );
});

const totalLeads = computed(() => filteredLeads.value.length);
const totalPages = computed(() => Math.max(1, Math.ceil(totalLeads.value / Math.max(1, Number(itemsPerPage.value || 1)))));
const paginatedLeads = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredLeads.value.slice(start, start + itemsPerPage.value);
});
const startIndex = computed(() => {
  if (!totalLeads.value) return 0;
  return ((currentPage.value - 1) * itemsPerPage.value) + 1;
});
const endIndex = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalLeads.value));

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value += 1;
};
const prevPage = () => {
  if (currentPage.value > 1) currentPage.value -= 1;
};

const leadInitials = (lead) => {
  const first = String(lead.firstName || '').trim().charAt(0).toUpperCase();
  const last = String(lead.lastName || '').trim().charAt(0).toUpperCase();
  return `${first}${last}`.trim() || 'L';
};

const statusLabel = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'onboarding') return 'Onboarding';
  if (token === 'pending_payment') return 'Pending Payment';
  return 'New';
};

const statusBadgeClass = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'pending_payment') return 'bg-amber-50 text-amber-700 ring-amber-600/20';
  if (token === 'onboarding') return 'bg-blue-50 text-blue-700 ring-blue-600/20';
  return 'bg-zinc-100 text-zinc-700 ring-zinc-500/20';
};

const sourceLabel = (source) => {
  const token = String(source || '').trim();
  if (!token) return 'Unknown';
  return token.replace(/_/g, ' ');
};

const goToLeadDetails = (lead) => {
  if (!lead?.id) return;
  window.dispatchEvent(new CustomEvent('lead-selected', {
    detail: {
      id: lead.id,
      name: lead.name,
    },
  }));
  router.push({ name: 'LeadDetails', params: { leadId: lead.id } });
};

onMounted(() => {
  void fetchLeads();
  if (typeof window !== 'undefined') {
    window.addEventListener('creator:member-lifecycle', onMemberLifecycle);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('creator:member-lifecycle', onMemberLifecycle);
  }
  if (lifecycleRefreshTimer) {
    clearTimeout(lifecycleRefreshTimer);
    lifecycleRefreshTimer = null;
  }
});

watch([searchQuery, itemsPerPage], () => {
  currentPage.value = 1;
});

watch(totalPages, (nextTotal) => {
  if (currentPage.value > nextTotal) {
    currentPage.value = nextTotal;
  }
});
</script>
